import React, { useContext, useEffect, useReducer } from 'react'
import {
  Accordion,
  AccordionItem,
  Button,
  ButtonType,
  FormFieldGroup,
  TextInput,
} from '@resurs-internal/styx-react'
import { toast } from 'react-toastify'
import { actOnData, classNames } from '../../common/utils'
import { uuid } from '../utils/testUtils'
import { PageContext } from './ContentContext'
import { EventEditor } from './EventEditor'
import { RequestEditor } from './RequestEditor'

import commonStyles from './styles.module.scss'
import styles from './PageEditor.module.scss'

export const PageStatus = {
  DRAFT: 'DRAFT',
  PUBLISHED: 'PUBLISHED',
  ARCHIVED: 'ARCHIVED',
}

const initialPageState = () => ({
  path: uuid(),
  parentPath: null,
  name: '',
  title: '',
  hideNav: false,
  language: 'en',
  status: PageStatus.PUBLISHED,
  content: [],
  order: 9999,
  dirty: true,
})

export const GizmoType = {
  REQUEST_MOCK: 'REQUEST_MOCK',
  EVENT_MOCK: 'EVENT_MOCK',
}

const Action = {
  SET_PATH: 'SET_PATH',
  SET_PARENT_PATH: 'SET_PARENT_PATH',
  SET_NAME: 'SET_NAME',
  SET_TITLE: 'SET_TITLE',
  SET_ORDER: 'SET_ORDER',
  SET_HIDE_NAV: 'SET_HIDE_NAV',
  SET_LANGUAGE: 'SET_LANGUAGE',
  SET_STATUS: 'SET_STATUS',
  ADD_GIZMO: 'ADD_GIZMO',
  UPDATE_GIZMO: 'UPDATE_GIZMO',
  REMOVE_GIZMO: 'REMOVE_GIZMO',
  MOVE_GIZMO: 'MOVE_GIZMO',
  LOAD_PAGE: 'LOAD_PAGE',
  GENERATE_ID: 'GENERATE_ID',
}

const pageReducer = (state, action) => {
  switch (action.type) {
    case Action.SET_PATH:
      return action.value === state.path
        ? state
        : { ...state, path: action.value, dirty: true }
    case Action.SET_PARENT_PATH:
      return action.value === state.parentPath
        ? state
        : { ...state, parentPath: action.value, dirty: true }
    case Action.SET_NAME:
      return action.value === state.name
        ? state
        : { ...state, name: action.value, dirty: true }
    case Action.SET_TITLE:
      return action.value === state.title
        ? state
        : { ...state, title: action.value, dirty: true }
    case Action.SET_ORDER:
      return +action.value === state.order
        ? state
        : { ...state, order: action.value, dirty: true }
    case Action.SET_HIDE_NAV:
      return action.value === state.hideNav
        ? state
        : { ...state, hideNav: action.value, dirty: true }
    case Action.SET_LANGUAGE:
      return action.value === state.language
        ? state
        : { ...state, language: action.value, dirty: true }
    case Action.SET_STATUS:
      return action.value === state.status
        ? state
        : { ...state, status: action.value, dirty: true }
    case Action.ADD_GIZMO:
      return {
        ...state,
        content: [
          ...state.content,
          {
            ...action.value,
            isNew: true,
          },
        ],
        dirty: true,
      }
    case Action.UPDATE_GIZMO:
      return {
        ...state,
        content: [
          ...state.content.map(gizmo =>
            gizmo.props.id === action.value.props.id ? action.value : gizmo,
          ),
        ],
        dirty: true,
      }
    case Action.REMOVE_GIZMO:
      return {
        ...state,
        content: state.content.filter(gizmo => gizmo.props.id !== action.value),
        dirty: true,
      }
    case Action.MOVE_GIZMO: {
      const gizmoToMove = state.content.find(
        gizmo => gizmo.props.id === action.value,
      )
      const other = state.content.filter(
        gizmo => gizmo.props.id !== action.value,
      )
      const prepend = other.slice(0, action.index)
      const append = other.slice(action.index)
      return {
        ...state,
        content: [...prepend, gizmoToMove, ...append],
        dirty: true,
      }
    }
    case Action.LOAD_PAGE:
      return {
        ...action.value,
        changelog: [],
        content: action.value?.content || [],
        dirty: false,
      }
    case Action.GENERATE_ID:
      return {
        ...state,
        content: [
          ...state.content.map(gizmo => ({
            ...gizmo,
            props: { ...gizmo.props, id: gizmo.props.id || uuid() },
          })),
        ],
        dirty: true,
      }
    default:
      return state
  }
}

export const PageEditor = ({ pageId, onClose }) => {
  const [state, dispatch] = useReducer(pageReducer, initialPageState())
  const pageContext = useContext(PageContext)

  const fetchPage = () =>
    (pageId ? pageContext.getPage(pageId) : Promise.resolve(initialPageState()))
      .then(json => dispatch({ type: Action.LOAD_PAGE, value: json }))
      .catch(() => {
        toast(`Unable to fetch page: ${pageId}`, {
          type: 'error',
          className: commonStyles.toastError,
        })
      })

  const savePage = () =>
    (pageId ? pageContext.savePage(state) : pageContext.addPage(state))
      .then(resp => {
        dispatch({ type: Action.LOAD_PAGE, value: resp })
        toast(`Scenario ${resp?.name} updated`, {
          type: 'success',
          className: commonStyles.toastSuccess,
        })
      })
      .catch(() => {
        toast(`Unable to save ${state?.name}`, {
          type: 'error',
          className: commonStyles.toastError,
        })
      })

  useEffect(() => {
    fetchPage().catch(() => {
      dispatch({ type: Action.LOAD_PAGE, value: initialPageState() })
    })
  }, [pageId])

  useEffect(() => {
    const func = event => {
      if (state.dirty) {
        // eslint-disable-next-line no-param-reassign
        event.returnValue = true
      }
    }
    window.addEventListener('beforeunload', func)
    return () => {
      window.removeEventListener('beforeunload', func)
    }
  }, [])

  const eventMocks = state.content?.filter(
    ({ type }) => type === GizmoType.EVENT_MOCK,
  )
  const requestMocks = state.content?.filter(
    ({ type }) => type === GizmoType.REQUEST_MOCK,
  )

  const renderGizmo = gizmo => {
    switch (gizmo.type) {
      case GizmoType.REQUEST_MOCK:
        return (
          <RequestEditor
            key={`gizmo_${gizmo.props.id}`}
            value={gizmo.props}
            isNew={gizmo.isNew}
            events={eventMocks}
            onChange={value =>
              dispatch({
                type: Action.UPDATE_GIZMO,
                value: {
                  ...gizmo,
                  props: { ...gizmo.props, ...value },
                },
              })
            }
            onDelete={() =>
              dispatch({
                type: Action.REMOVE_GIZMO,
                value: gizmo.props.id,
              })
            }
            onCopy={() =>
              dispatch({
                type: Action.ADD_GIZMO,
                value: {
                  ...gizmo,
                  props: {
                    ...gizmo.props,
                    id: uuid(),
                    description: `${gizmo.props.description} copy`,
                  },
                },
              })
            }
          />
        )
      case GizmoType.EVENT_MOCK:
        return (
          <EventEditor
            key={`gizmo_${gizmo.props.id}`}
            value={gizmo.props}
            isNew={gizmo.isNew}
            onChange={value =>
              dispatch({
                type: Action.UPDATE_GIZMO,
                value: {
                  ...gizmo,
                  props: { ...gizmo.props, ...value },
                },
              })
            }
            onDelete={() =>
              dispatch({
                type: Action.REMOVE_GIZMO,
                value: gizmo.props.id,
              })
            }
            onCopy={() =>
              dispatch({
                type: Action.ADD_GIZMO,
                value: {
                  ...gizmo,
                  props: {
                    ...gizmo.props,
                    id: uuid(),
                    description: `${gizmo.props.description} copy`,
                  },
                },
              })
            }
          />
        )
      default:
        return (
          <p key={`gizmo_${gizmo.props.id}`}>Unknown gizmo: {gizmo.type}</p>
        )
    }
  }

  return (
    <div className={styles.container}>
      <div key={`editor_${state.id}`} className={commonStyles.flexCol}>
        <FormFieldGroup>
          <TextInput
            id="page_name"
            label="Scenario name"
            required
            value={state.name}
            onChange={(id, value) => dispatch({ type: Action.SET_NAME, value })}
          />
          <TextInput
            id="page_order"
            label="Order"
            value={state.order}
            type="number"
            onChange={(id, value) =>
              dispatch({ type: Action.SET_ORDER, value })
            }
          />
        </FormFieldGroup>

        {actOnData(
          state.content?.filter(gizmo => !gizmo.props.id),
          missingIds => (
            <div className={commonStyles.flexEnd}>
              <Button
                id="generate-ids"
                onClick={() => dispatch({ type: Action.GENERATE_ID })}
                disabled={!missingIds.length}
              >
                Generate Gizmo ID
              </Button>
            </div>
          ),
        )}
        {!0 && (
          <>
            <h4>Flow events</h4>
            {(eventMocks.length && (
              <Accordion>
                {eventMocks.map(gizmo => (
                  <AccordionItem
                    id={`acc_gizmo_${gizmo.props.id}`}
                    key={`acc_gizmo_${gizmo.props.id}`}
                    label={gizmo.props.description}
                    expand={gizmo.isNew}
                  >
                    {renderGizmo(gizmo)}
                  </AccordionItem>
                ))}
              </Accordion>
            )) || <p>No mocked events</p>}
            <div className={commonStyles.flexEnd}>
              <Button
                id="add_flow_event"
                onClick={() =>
                  dispatch({
                    type: Action.ADD_GIZMO,
                    value: {
                      type: GizmoType.EVENT_MOCK,
                      props: { id: uuid() },
                    },
                  })
                }
              >
                Add event
              </Button>
            </div>
            <h4>Requests</h4>
            {(requestMocks.length && (
              <Accordion>
                {requestMocks.map(gizmo => (
                  <AccordionItem
                    id={`acc_gizmo_${gizmo.props.id}`}
                    key={`acc_gizmo_${gizmo.props.id}`}
                    label={
                      classNames(
                        gizmo.props.method,
                        gizmo.props.path,
                        actOnData(gizmo.props.description, desc => `(${desc})`),
                      ) || '[new]'
                    }
                    expand={gizmo.isNew}
                  >
                    {renderGizmo(gizmo)}
                  </AccordionItem>
                ))}
              </Accordion>
            )) || <p>No mocked requests</p>}
            <div className={commonStyles.flexEnd}>
              <Button
                id="add_gizmo"
                onClick={() =>
                  dispatch({
                    type: Action.ADD_GIZMO,
                    value: {
                      type: GizmoType.REQUEST_MOCK,
                      props: { id: uuid() },
                    },
                  })
                }
              >
                Add request
              </Button>
            </div>
          </>
        )}
        <div className={commonStyles.flexRow} />
      </div>
      <div className={styles.footer}>
        {state.id && (
          <Button
            id="delete_page"
            type={ButtonType.CallToAction}
            onClick={() => {
              // eslint-disable-next-line no-alert
              if (window.confirm('Are you sure you want to delete?')) {
                pageContext
                  .archivePage(state)
                  .then(resp => {
                    toast(`Scenario ${resp?.name} archived`, {
                      type: 'success',
                      className: commonStyles.toastSuccess,
                    })
                  })
                  .finally(() => {
                    onClose()
                  })
              }
            }}
          >
            Delete page
          </Button>
        )}
        <Button id="save_page" onClick={savePage} disabled={!state.dirty}>
          Save page
        </Button>
      </div>
    </div>
  )
}
